import { rejectSession } from "./session";

export const API_BASE = (
	import.meta.env.VITE_PIONEER_API_URL ||
	(import.meta.env.DEV ? "" : "https://alpha.pioneers.dev")
).replace(/\/$/, "");
export interface Session {
	token: string;
	address: string;
	expiresAt?: number;
}
interface Ethereum {
	request(args: { method: string; params?: unknown[] }): Promise<unknown>;
	on?(event: string, callback: (accounts: string[]) => void): void;
	removeListener?(event: string, callback: (accounts: string[]) => void): void;
}
declare global {
	interface Window {
		ethereum?: Ethereum;
	}
}
export class ApiError extends Error {
	constructor(
		message: string,
		public status: number,
	) {
		super(message);
	}
}
export async function request<T>(
	path: string,
	session?: Session,
	body?: unknown,
	{ timeout = 12_000, headers = {} }: { timeout?: number; headers?: Record<string, string> } = {},
): Promise<T> {
	const response = await fetch(`${API_BASE}${path}`, {
		method: body === undefined ? "GET" : "POST",
		headers: {
			...(body === undefined ? {} : { "content-type": "application/json" }),
			...(session ? { authorization: `Bearer ${session.token}` } : {}),
			...headers,
		},
		body: body === undefined ? undefined : JSON.stringify(body),
		signal: AbortSignal.timeout(timeout),
	});
	if (response.status === 401 && session) rejectSession(session.token);
	const data = await response.json();
	// Meetings answer {error: "text"}; the agents API answers {error: {code, message}}.
	if (!response.ok) {
		throw new ApiError(
			(typeof data.error === "string" ? data.error : data.error?.message) || "Request failed",
			response.status,
		);
	}
	return data;
}
export async function signIn(): Promise<Session> {
	if (!window.ethereum)
		throw new Error(
			"Open this page in a wallet browser or connect a browser wallet such as MetaMask or KeepKey.",
		);
	const accounts = (await window.ethereum.request({
		method: "eth_requestAccounts",
	})) as string[];
	const address = accounts[0];
	if (!address) throw new Error("No wallet account selected");
	const { challenge, nonce } = await request<{
		challenge: string;
		nonce: string;
	}>("/auth/challenge");
	const message = `Pioneer API\nChallenge: ${challenge}\nNonce: ${nonce}\nAddress: ${address}`;
	const signature = await window.ethereum.request({
		method: "personal_sign",
		params: [message, address],
	});
	const { token, expiresAt } = await request<{ token: string; expiresAt?: number }>(
		"/auth/verify",
		undefined,
		{ address, signature, challenge, nonce },
	);
	return { token, address: address.toLowerCase(), expiresAt };
}
export interface Peer {
	id: string;
	address: string;
	name: string;
	isMember: boolean;
	recorder: boolean;
	camera: boolean;
	screen: boolean;
}
export interface RoomState {
	organizer: string;
	id: string;
	company: string;
	title: string;
	startedAt: number;
	endedAt: number | null;
	guestDeadline: number | null;
	recorderOnline: boolean;
	archiveCompany?:string;
	archive?: {state:"saved"|"pending";revision:number;delivered_revision:number;last_error:string|null;delivered_at:number|null}|null;
	videoRecording: boolean;
	peers: Peer[];
	cursor: number;
	iceServers?: RTCIceServer[];
	signals: {
		seq: number;
		from: string;
		data: {
			description?: RTCSessionDescriptionInit;
			candidate?: RTCIceCandidateInit;
		};
	}[];
	chat: {
		id: string;
		address: string;
		name: string;
		text: string;
		at: number;
		agent?: boolean;
	}[];
	// SPEC-029 M3: AI participants (talking heads) and the lines they speak.
	agents: AgentSeat[];
	speech: Line[];
}
export interface AgentSeat {
	slug: string;
	name: string;
}
export interface Line {
	id: string;
	speaker: string;
	text: string;
	mood: string;
	clip: string | null;
	at: number;
}
